// src/components/Contact.jsx
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, Clock } from "lucide-react";
import { useState } from "react";
import TextReveal from "./animations/TextReveal";
import Magnetic from "./animations/Magnetic";
import TiltCard from "./animations/TiltCard";

const contactInfo = [
  {
    icon: Phone,
    title: "Phone",
    details: "+216 24 494 492",
    subDetails: "Sun–Thu  ·  8:00 – 17:00",
  },
  {
    icon: Mail,
    title: "Email",
    details: "sb.isims@ieee.org",
    subDetails: "sb.isims@ieee.org",
  },
  {
    icon: MapPin,
    title: "Location",
    details: "Sfax, rue Tunis klm 11",
    subDetails: "Sakiet Ezzit, Sfax 3021",
  },
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    setFormData({ name: "", email: "", subject: "", message: "" });
    alert("Message sent successfully!");
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div id="contact" className="relative bg-white w-full overflow-hidden pt-10 pb-16 px-4 sm:px-8 lg:px-16 flex items-center justify-center">
      {/* Background */}
      <div className="absolute inset-0 -z-10 bg-[#fafcff]">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "url('https://grainy-gradients.vercel.app/noise.svg')" }}
        />
        {/* Subtle grid texture instead of gradients */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              repeating-linear-gradient(
                -45deg,
                rgba(15, 105, 157, 0.02) 0px,
                rgba(15, 105, 157, 0.02) 1px,
                transparent 1px,
                transparent 18px
              )
            `,
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative w-full max-w-6xl"
      >
        {/* Outer card - Sharp corners, solid borders */}
        <div className="relative border-2 border-[#006699]/10 bg-white/90 backdrop-blur-md p-8 sm:p-12 lg:p-16 shadow-xl overflow-hidden rounded-none">
          
          {/* L borders accents */}
          <div className="absolute top-0 left-0 w-12 h-12 border-l-2 border-t-2 border-[#006699]/30 pointer-events-none" />
          <div className="absolute top-0 right-0 w-12 h-12 border-r-2 border-t-2 border-[#006699]/30 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-12 h-12 border-l-2 border-b-2 border-[#006699]/30 pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-12 h-12 border-r-2 border-b-2 border-[#006699]/30 pointer-events-none" />

          <div className="relative z-10">
            {/* Header */}
            <div className="text-center mb-12">
              <p className="text-xs font-medium tracking-[0.3em] text-[#006699]/70 uppercase mb-3">
                IEEE ISIMS Student Branch
              </p>
              <TextReveal
                as="h1"
                className="text-4xl sm:text-5xl lg:text-6xl text-[#00334d] mb-5 tracking-tight font-medium"
                stagger={0.06}
              >
                Get In Touch
              </TextReveal>
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: 80 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="h-1 bg-[#006699] mx-auto opacity-80"
              />
            </div>

            {/* Contact Info Cards */}
            <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mb-12">
              {contactInfo.map((item, index) => {
                const Icon = item.icon;
                return (
                  <TiltCard key={index} maxTilt={9} scale={1.03}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                      className="flex flex-col items-center text-center p-6 bg-white border border-[#006699]/10 hover:border-[#006699]/40 hover:shadow-lg transition-all duration-300 group rounded-none h-full"
                    >
                      <div
                        className={`p-3 bg-[#f0f7fa] border border-[#006699]/10 mb-4 transition-colors duration-300 group-hover:bg-[#006699] rounded-none`}
                      >
                        <Icon className="w-5 h-5 text-[#006699] group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                      </div>
                      <h3
                        className="text-xs font-semibold tracking-[0.15em] text-[#00334d] uppercase mb-2"
                      >
                        {item.title}
                      </h3>
                      <p className="text-slate-600 text-sm font-medium">{item.details}</p>
                      <p className="text-slate-400 text-xs mt-1">{item.subDetails}</p>
                    </motion.div>
                  </TiltCard>
                );
              })}
            </div>

            {/* Form + Map */}
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="bg-white border border-[#006699]/10 p-6 sm:p-8 rounded-none"
              >
                <h2
                  className="text-lg font-semibold tracking-wide text-[#00334d] mb-6 flex items-center gap-2"
                >
                  <Send className="w-4 h-4 text-[#006699]" strokeWidth={2} />
                  Send a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold tracking-widest text-[#006699]/80 uppercase mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-[#f8fbff] border border-[#006699]/15 focus:outline-none focus:border-[#006699] transition-all duration-300 text-sm text-[#00334d] placeholder-slate-400 rounded-none"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold tracking-widest text-[#006699]/80 uppercase mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-[#f8fbff] border border-[#006699]/15 focus:outline-none focus:border-[#006699] transition-all duration-300 text-sm text-[#00334d] placeholder-slate-400 rounded-none"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold tracking-widest text-[#006699]/80 uppercase mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-[#f8fbff] border border-[#006699]/15 focus:outline-none focus:border-[#006699] transition-all duration-300 text-sm text-[#00334d] placeholder-slate-400 rounded-none"
                      placeholder="Message subject"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold tracking-widest text-[#006699]/80 uppercase mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="4"
                      className="w-full px-4 py-3 bg-[#f8fbff] border border-[#006699]/15 focus:outline-none focus:border-[#006699] transition-all duration-300 resize-none text-sm text-[#00334d] placeholder-slate-400 rounded-none"
                      placeholder="Your message here…"
                    />
                  </div>

                  <Magnetic strength={0.3} className="w-full">
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full py-3.5 bg-[#006699] hover:bg-[#004d73] text-white font-medium tracking-widest text-sm uppercase transition-all duration-300 flex items-center justify-center gap-2 rounded-none border border-[#006699] cursor-pointer"
                    >
                      <Send className="w-4 h-4" strokeWidth={1.5} />
                      Send Message
                    </motion.button>
                  </Magnetic>
                </form>
              </motion.div>

              {/* Map */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="bg-white border border-[#006699]/10 p-6 sm:p-8 flex flex-col rounded-none"
              >
                <h2 className="text-lg font-semibold tracking-wide text-[#00334d] mb-1 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#006699]" strokeWidth={2} />
                  Our Location
                </h2>
                <p className="text-slate-500 text-xs font-medium mb-6">
                  ISIMS, Pôle Technologique de Sfax, Sakiet Ezzit 3021
                </p>

                <div className="flex-1 min-h-[300px] overflow-hidden border border-[#006699]/15 rounded-none p-1 bg-[#f8fbff]">
                  <iframe
                    title="ISIMS Location"
                    src="https://maps.google.com/maps?q=34.83882,10.75725&z=16&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0, minHeight: "300px" }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="grayscale hover:grayscale-0 transition-all duration-700"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}